import { ChevronLeft, ChevronRight, ChevronTop } from "@monta/icons";
import clsx from "clsx";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useState } from "react";
import { type DatepickerProps } from "..";
import Calendar, { type TCalendarValue, useCalendar } from "../../../Calendar";
import Card from "../../../Card";
import styles from "../Datepicker.module.scss";

interface OptionSelectorProps<T extends string | number> {
  items: T[];
  value: T;
  onChange: (value: T) => void;
}

function OptionSelector<T extends string | number>(
  props: OptionSelectorProps<T>,
) {
  const { items, onChange, value } = props;
  return (
    <div className={styles["options"]}>
      {items.map((item) => (
        <div
          key={item + "_option"}
          className={clsx(
            styles["options__item"],
            item === value && styles["options__item--active"],
          )}
          onClick={() => onChange(item)}
        >
          {item}
        </div>
      ))}
    </div>
  );
}

const FlatDatePicker = (
  props: DatepickerProps & {
    hasFooterBtns?: boolean;
    close?: () => void;
  },
) => {
  const { value, onChange, range, hasFooterBtns = true, close } = props;
  const [selectingYear, setSelectingYear] = useState(false);

  const [monthIndex, setMonthIndex] = useState(
    ((Array.isArray(value) ? value[0] : value) || dayjs())
      .calendar("jalali")
      .month(),
  );
  const [year, setYear] = useState(
    +((Array.isArray(value) ? value[0] : value) || dayjs()).standardFormat({
      jalali: true,
      template: "YYYY",
    }),
  );
  const { months } = useCalendar({
    monthIndex,
    year,
  });
  const rangeArray = (min, max) =>
    Array.from({ length: max - min + 1 }, (_, i) => min + i);

  const dateClickHandler = (date: Dayjs) => {
    if (!onChange) return;
    if (range) {
      if (!value?.[0] || (value?.[0] && value?.[1]) || date <= value?.[0])
        onChange([date, null]);
      else if (value?.[0] && !value?.[1]) onChange([value[0], date]);
    } else {
      onChange(date);
    }
  };

  return (
    <Card className={styles["datepicker"]}>
      <div className={styles["datepicker__header"]}>
        <button
          onClick={() => {
            if (monthIndex == 0) {
              setMonthIndex(11);
              setYear(year - 1);
            } else {
              setMonthIndex(monthIndex - 1);
            }
          }}
          className={styles["datepicker__header__month-nav"]}
        >
          <ChevronRight />
          ماه قبل
        </button>
        <button
          className={clsx(
            styles["datepicker__header__year"],
            selectingYear && styles["datepicker__header__year--active"],
          )}
          onClick={() => setSelectingYear(!selectingYear)}
        >
          {months[monthIndex].name} {year} <ChevronTop />
        </button>
        <button
          onClick={() => {
            if (monthIndex == 11) {
              setMonthIndex(0);
              setYear(year + 1);
            } else {
              setMonthIndex(monthIndex + 1);
            }
          }}
          className={styles["datepicker__header__month-nav"]}
        >
          ماه بعد <ChevronLeft />
        </button>
      </div>
      {selectingYear ? (
        <OptionSelector
          items={rangeArray(year - 8, year + 11)}
          value={year}
          onChange={(value) => {
            setSelectingYear(false);
            setYear(value);
          }}
        />
      ) : (
        <Calendar
          monthIndex={monthIndex}
          year={year}
          value={value}
          onDateClick={dateClickHandler}
        />
      )}
      {hasFooterBtns && (
        <div className={styles["datepicker__footer"]}>
          <button
            onClick={() => {
              if (range) onChange?.([null, null]);
              else onChange?.(null);
            }}
          >
            پاک کردن
          </button>
          <button
            onClick={close}
            disabled={
              range
                ? (value as Array<TCalendarValue>)?.some?.((i) => !i)
                : !value
            }
          >
            تائید
          </button>
        </div>
      )}
    </Card>
  );
};

export default FlatDatePicker;
